import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Tag, Popconfirm, Modal, Form, Input, InputNumber, Switch, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

interface PromoCodeOption {
    id: number;
    code: string;
}

interface SubscriptionPlan {
    id: number;
    name: string;
    description: string | null;
    price: number;
    durationDays: number | null;
    maxTournaments: number | null;
    maxParticipants: number | null;
    isConfigurable?: boolean;
    pricePerTournament?: number | null;
    pricePerParticipant?: number | null;
    features: string[] | null;
    isActive: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    promoCodes?: PromoCodeOption[];
}

const SubscriptionPlansPage: React.FC = () => {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [promoCodes, setPromoCodes] = useState<PromoCodeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
    const [form] = Form.useForm();

    const fetchPromoCodes = async () => {
        try {
            const res = await apiService.get<ApiResponse<PromoCodeOption[]>>(API_ENDPOINTS.PROMO_CODES.BASE);
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            setPromoCodes(list);
        } catch {
            setPromoCodes([]);
        }
    };

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<ApiResponse<SubscriptionPlan[]>>(
                API_ENDPOINTS.SUBSCRIPTION_PLANS.BASE
            );
            const plans = Array.isArray(response.data.data) ? response.data.data : [];
            // Нормализуем features: если это строка JSON, парсим её; если null/undefined, оставляем null; если массив, оставляем как есть
            const normalizedPlans = plans.map(plan => ({
                ...plan,
                features: (() => {
                    if (plan.features === null || plan.features === undefined) return null;
                    if (Array.isArray(plan.features)) return plan.features;
                    if (typeof plan.features === 'string') {
                        try {
                            const parsed = JSON.parse(plan.features);
                            return Array.isArray(parsed) ? parsed : null;
                        } catch (e) {
                            return null;
                        }
                    }
                    return null;
                })()
            }));
            setPlans(normalizedPlans);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 500 || status === 404) {
                logger.warn('Subscription plans unavailable (server or schema). Showing empty list.', { status });
            } else {
                logger.error('Error fetching subscription plans:', error);
            }
            message.warning('Планы подписок временно недоступны. Проверьте миграции БД на сервере.');
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);
    useEffect(() => {
        if (isModalVisible) fetchPromoCodes();
    }, [isModalVisible]);

    const handleCreate = () => {
        setEditingPlan(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (plan: SubscriptionPlan) => {
        setEditingPlan(plan);
        form.setFieldsValue({
            name: plan.name,
            description: plan.description,
            price: parseFloat(plan.price.toString()),
            durationDays: plan.durationDays,
            maxTournaments: plan.maxTournaments,
            maxParticipants: plan.maxParticipants,
            isConfigurable: plan.isConfigurable ?? false,
            pricePerTournament: plan.pricePerTournament != null ? parseFloat(String(plan.pricePerTournament)) : undefined,
            pricePerParticipant: plan.pricePerParticipant != null ? parseFloat(String(plan.pricePerParticipant)) : undefined,
            features: plan.features?.join('\n') || '',
            isActive: plan.isActive,
            sortOrder: plan.sortOrder,
            promoCodeIds: (plan as SubscriptionPlan).promoCodes?.map((p: PromoCodeOption) => p.id) || []
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await apiService.delete(API_ENDPOINTS.SUBSCRIPTION_PLANS.DELETE(id));
            message.success('План подписки успешно удален');
            fetchPlans();
        } catch (error: any) {
            logger.error('Error deleting subscription plan:', error);
            const errorMessage = error?.response?.data?.message || 'Ошибка при удалении плана подписки';
            message.error(errorMessage);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // Валидация: должно быть хотя бы одно ограничение
            if ((!values.durationDays || values.durationDays === null || values.durationDays === '') && 
                (!values.maxTournaments || values.maxTournaments === null || values.maxTournaments === '') &&
                (!values.maxParticipants || values.maxParticipants === null || values.maxParticipants === '')) {
                message.error('Необходимо указать хотя бы одно ограничение: длительность, лимит создания турниров или лимит регистраций участников');
                return;
            }

            const isConfigurable = !!values.isConfigurable;
            if (!isConfigurable && (values.price == null || values.price === '')) {
                message.error('Укажите цену для тарифа');
                return;
            }
            if (isConfigurable) {
                const hasPricePerTournament = values.pricePerTournament != null && values.pricePerTournament !== '';
                const hasPricePerParticipant = values.pricePerParticipant != null && values.pricePerParticipant !== '';
                if (!hasPricePerTournament && !hasPricePerParticipant) {
                    message.error('Для настраиваемого тарифа укажите цену за 1 турнир и/или цену за 1 участника');
                    return;
                }
            }
            const planData = {
                ...values,
                features: values.features ? values.features.split('\n').filter((f: string) => f.trim()) : null,
                price: isConfigurable ? (values.price != null && values.price !== '' ? parseFloat(values.price.toString()) : 0) : parseFloat(values.price.toString()),
                durationDays: values.durationDays === '' || values.durationDays === null || values.durationDays === undefined ? null : parseInt(values.durationDays.toString()),
                maxTournaments: values.maxTournaments === '' || values.maxTournaments === null || values.maxTournaments === undefined ? null : parseInt(values.maxTournaments.toString()),
                maxParticipants: values.maxParticipants === '' || values.maxParticipants === null || values.maxParticipants === undefined ? null : parseInt(values.maxParticipants.toString()),
                isConfigurable,
                pricePerTournament: values.pricePerTournament === '' || values.pricePerTournament === null || values.pricePerTournament === undefined ? null : parseFloat(values.pricePerTournament.toString()),
                pricePerParticipant: values.pricePerParticipant === '' || values.pricePerParticipant === null || values.pricePerParticipant === undefined ? null : parseFloat(values.pricePerParticipant.toString()),
                promoCodeIds: Array.isArray(values.promoCodeIds) ? values.promoCodeIds : []
            };

            if (editingPlan) {
                await apiService.put(
                    API_ENDPOINTS.SUBSCRIPTION_PLANS.UPDATE(editingPlan.id),
                    planData
                );
                message.success('План подписки успешно обновлен');
            } else {
                await apiService.post(
                    API_ENDPOINTS.SUBSCRIPTION_PLANS.CREATE,
                    planData
                );
                message.success('План подписки успешно создан');
            }

            setIsModalVisible(false);
            form.resetFields();
            fetchPlans();
        } catch (error: any) {
            logger.error('Error saving subscription plan:', error);
            const errorMessage = error?.response?.data?.message || 'Ошибка при сохранении плана подписки';
            message.error(errorMessage);
        }
    };

    const columns: ColumnsType<SubscriptionPlan> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по названию"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Цена (₽)',
            dataIndex: 'price',
            key: 'price',
            render: (price: number) => `${parseFloat(price.toString()).toFixed(2)}`,
            sorter: (a, b) => parseFloat(a.price.toString()) - parseFloat(b.price.toString()),
        },
        {
            title: 'Длительность (дней)',
            dataIndex: 'durationDays',
            key: 'durationDays',
            render: (durationDays: number | null) => durationDays === null ? 'Неограниченно' : durationDays,
            sorter: (a, b) => {
                if (a.durationDays === null) return 1;
                if (b.durationDays === null) return -1;
                return a.durationDays - b.durationDays;
            },
        },
        {
            title: 'Лимит создания турниров',
            dataIndex: 'maxTournaments',
            key: 'maxTournaments',
            render: (maxTournaments: number | null) => maxTournaments === null ? 'Неограниченно' : maxTournaments,
            sorter: (a, b) => {
                if (a.maxTournaments === null) return 1;
                if (b.maxTournaments === null) return -1;
                return a.maxTournaments - b.maxTournaments;
            },
        },
        {
            title: 'Лимит регистраций участников',
            dataIndex: 'maxParticipants',
            key: 'maxParticipants',
            render: (maxParticipants: number | null) => maxParticipants === null ? 'Неограниченно' : maxParticipants,
            sorter: (a, b) => {
                if (a.maxParticipants === null) return 1;
                if (b.maxParticipants === null) return -1;
                return a.maxParticipants - b.maxParticipants;
            },
        },
        {
            title: 'Возможности',
            dataIndex: 'features',
            key: 'features',
            render: (features: string[] | string | null) => {
                // Если features - это строка JSON, парсим её
                let featuresArray: string[] = [];
                if (typeof features === 'string') {
                    try {
                        featuresArray = JSON.parse(features);
                    } catch (e) {
                        // Если не JSON, предполагаем что это одна строка
                        featuresArray = features ? [features] : [];
                    }
                } else if (Array.isArray(features)) {
                    featuresArray = features;
                } else if (features === null || features === undefined) {
                    return '-';
                }
                
                if (!featuresArray || featuresArray.length === 0) return '-';
                return (
                    <div>
                        {featuresArray.slice(0, 2).map((f, i) => (
                            <Tag key={i}>{f}</Tag>
                        ))}
                        {featuresArray.length > 2 && <Tag>+{featuresArray.length - 2}</Tag>}
                    </div>
                );
            },
        },
        {
            title: 'Активен',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Да' : 'Нет'}
                </Tag>
            ),
            filters: [
                { text: 'Активные', value: true },
                { text: 'Неактивные', value: false },
            ],
            onFilter: (value, record) => record.isActive === value,
        },
        {
            title: 'Порядок',
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            sorter: (a, b) => a.sortOrder - b.sortOrder,
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: SubscriptionPlan) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Вы уверены, что хотите удалить этот план подписки?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Планы подписок</h2>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Создать план подписки
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={plans}
                rowKey="id"
                loading={loading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} планов`,
                }}
            />

            <Modal
                title={editingPlan ? 'Редактировать план подписки' : 'Создать план подписки'}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                width={600}
                okText={editingPlan ? 'Сохранить' : 'Создать'}
                cancelText="Отмена"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        isActive: true,
                        sortOrder: 0,
                        durationDays: 30,
                        maxTournaments: null,
                        maxParticipants: null,
                        isConfigurable: false,
                        pricePerTournament: null,
                        pricePerParticipant: null,
                        promoCodeIds: []
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true, message: 'Пожалуйста, введите название' }]}
                    >
                        <Input placeholder="Например: Базовый план" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea rows={3} placeholder="Описание плана подписки" />
                    </Form.Item>

                    <Form.Item
                        name="price"
                        label="Цена (₽)"
                        tooltip="Для настраиваемого тарифа можно оставить 0 — считается из цены за турнир/участника"
                        rules={[
                            { type: 'number', min: 0, message: 'Цена должна быть неотрицательной' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="0.00 (для настраиваемого — 0)"
                            min={0}
                            step={100}
                            precision={2}
                        />
                    </Form.Item>

                    <Form.Item
                        name="durationDays"
                        label="Длительность (дней, null = без ограничения по времени)"
                        tooltip="Оставьте пустым для подписки без ограничения по времени. Должно быть указано хотя бы одно ограничение: длительность, лимит создания турниров или лимит регистраций участников."
                        normalize={(value) => {
                            if (value === '' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined;
                            return typeof value === 'number' ? value : undefined;
                        }}
                        rules={[
                            { type: 'number', min: 1, message: 'Длительность должна быть больше 0' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="null (без ограничения по времени)"
                            min={1}
                            step={1}
                            formatter={(value) => value === null || value === undefined ? '' : value.toString()}
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxTournaments"
                        label="Лимит создания турниров (null = неограниченно)"
                        tooltip="Оставьте пустым для неограниченного количества турниров для создания"
                        normalize={(value) => {
                            if (value === '' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined;
                            return typeof value === 'number' ? value : undefined;
                        }}
                        rules={[
                            { type: 'number', min: 0, message: 'Лимит создания турниров должен быть неотрицательным' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="null (неограниченно)"
                            min={0}
                            step={1}
                            formatter={(value) => value === null || value === undefined ? '' : value.toString()}
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxParticipants"
                        label="Лимит регистраций участников (null = неограниченно)"
                        tooltip="Оставьте пустым для неограниченного количества регистраций участников"
                        normalize={(value) => {
                            if (value === '' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined;
                            return typeof value === 'number' ? value : undefined;
                        }}
                        rules={[
                            { type: 'number', min: 0, message: 'Лимит регистраций участников должен быть неотрицательным' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="null (неограниченно)"
                            min={0}
                            step={1}
                            formatter={(value) => value === null || value === undefined ? '' : value.toString()}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isConfigurable"
                        label="Настраиваемый тариф (ползунки)"
                        valuePropName="checked"
                        tooltip="При включении пользователь выбирает количество турниров и участников ползунками; цена считается из цены за 1 турнир и за 1 участника"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.isConfigurable !== curr.isConfigurable}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('isConfigurable') ? (
                                <>
                                    <Form.Item
                                        name="pricePerTournament"
                                        label="Цена за 1 турнир (₽)"
                                        tooltip="Для настраиваемого тарифа — цена за один турнир"
                                        rules={[{ type: 'number', min: 0, message: 'Цена не может быть отрицательной' }]}
                                    >
                                        <InputNumber style={{ width: '100%' }} placeholder="0" min={0} step={50} precision={2} />
                                    </Form.Item>
                                    <Form.Item
                                        name="pricePerParticipant"
                                        label="Цена за 1 участника (₽)"
                                        tooltip="Для настраиваемого тарифа — цена за одного участника"
                                        rules={[{ type: 'number', min: 0, message: 'Цена не может быть отрицательной' }]}
                                    >
                                        <InputNumber style={{ width: '100%' }} placeholder="0" min={0} step={10} precision={2} />
                                    </Form.Item>
                                </>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        name="promoCodeIds"
                        label="Промокоды для этого плана"
                        tooltip="Выберите промокоды, которые можно применить к этому тарифу. У покупателя будет показано поле ввода промокода только если выбран хотя бы один."
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Выберите промокоды"
                            options={promoCodes.map(p => ({ label: `${p.code} (ID ${p.id})`, value: p.id }))}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="features"
                        label="Возможности (каждая на новой строке)"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Неограниченное количество турниров&#10;Приоритетная поддержка&#10;Расширенная статистика"
                        />
                    </Form.Item>

                    <Form.Item
                        name="sortOrder"
                        label="Порядок сортировки"
                        rules={[{ type: 'number', min: 0, message: 'Порядок должен быть неотрицательным' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="0"
                            min={0}
                            step={1}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Активен"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubscriptionPlansPage;

